import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  TablePagination,
} from '@material-ui/core';
import _ from 'lodash';

import EnhancedTableHead from './components/EnhanchedTableHead';
import EnhancedTableToolbar from './components/EnhancedTableToolbar';

const getSorting = (order, orderBy) => (order === 'desc'
  ? (a, b) => (b[orderBy] < a[orderBy] ? -1 : 1)
  : (a, b) => (a[orderBy] < b[orderBy] ? -1 : 1));

class DataTable extends React.Component {
  static propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    tableHeaders: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ),
    tableData: PropTypes.arrayOf(PropTypes.object),
    renderToolbars: PropTypes.func,
    disableSearchBar: PropTypes.bool,
  };

  static defaultProps = {
    title: '',
    subtitle: 'Total',
    tableHeaders: [],
    tableData: [],
    renderToolbars: () => {},
    disableSearchBar: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      order: 'asc',
      orderBy: props.tableHeaders[0] ? props.tableHeaders[0].value : '',
      selected: [],
      page: 0,
      rowsPerPage: 10,
      searchResult: props.tableData,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.order !== prevState.order || this.state.orderBy !== prevState.orderBy) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(state => ({
        searchResult: state.searchResult.sort(getSorting(state.order, state.orderBy)),
      }));
    }

    if (this.props.disableSearchBar && !_.isEqual(prevProps.tableData, this.props.tableData)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ searchResult: this.props.tableData });
    }
  }

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = 'asc';

    if (this.state.orderBy === property && this.state.order === 'asc') {
      order = 'desc';
    }

    this.setState({ order, orderBy });
  };

  handleSelectAllClick = (event, checked) => {
    if (checked) {
      this.setState({ selected: this.props.tableData.map(rowData => rowData.id) });
      return;
    }
    this.setState({ selected: [] });
  };

  handleClick = (event, id) => {
    const { selected } = this.state;
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    this.setState({ selected: newSelected });
  };

  handleChangePage = (event, page) => {
    this.setState({ page });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value });
  };

  isSelected = id => this.state.selected.indexOf(id) !== -1;

  resetSelectedRows = (except = []) => {
    this.setState({ selected: except });
  };

  render() {
    const {
      title, subtitle, tableHeaders, tableData, renderToolbars,
    } = this.props;

    const emptyRows = this.state.rowsPerPage
      - Math.min(
        this.state.rowsPerPage,
        this.state.searchResult.length - this.state.page * this.state.rowsPerPage,
      ); // eslint-disable-line max-len

    return (
      <Paper>
        <EnhancedTableToolbar
          tableTitle={title}
          tableSubtitle={subtitle}
          tableData={tableData}
          rowsNumber={this.state.searchResult.length}
          numSelected={this.state.selected.length}
          renderToolbars={() => renderToolbars(this.state.selected)}
          onSearch={(searchResult) => {
            this.setState({ searchResult });
          }}
          disableSearchBar={this.props.disableSearchBar}
        />
        <TableWrapper>
          <Table>
            <EnhancedTableHead
              headers={tableHeaders}
              numSelected={this.state.selected.length}
              order={this.state.order}
              orderBy={this.state.orderBy}
              onSelectAllClick={this.handleSelectAllClick}
              onRequestSort={this.handleRequestSort}
              rowCount={this.state.searchResult.length}
            />
            <TableBody>
              {this.state.searchResult
                .slice(
                  this.state.page * this.state.rowsPerPage,
                  this.state.page * this.state.rowsPerPage + this.state.rowsPerPage,
                )
                .map((rowData) => {
                  const isSelected = this.isSelected(rowData.id);
                  return (
                    <TableRow
                      hover
                      onClick={event => this.handleClick(event, rowData.id)}
                      role="checkbox"
                      aria-checked={isSelected}
                      tabIndex={-1}
                      key={rowData.id}
                      selected={isSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected} />
                      </TableCell>
                      {tableHeaders.map((header) => {
                        if (['resolutionDraft', 'amendmentName'].includes(header.value)) {
                          return (
                            <TableCell
                              key={header.value}
                              component="th"
                              scope="row"
                              padding="dense"
                              style={{ minWidth: 640 }}
                            >
                              {_.get(rowData, header.value, 'AB')}
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={header.value} component="th" scope="row" padding="dense">
                            {_.get(rowData, header.value, 'AB')}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 49 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableWrapper>

        <TablePagination
          component="div"
          count={this.state.searchResult.length}
          rowsPerPage={this.state.rowsPerPage}
          page={this.state.page}
          backIconButtonProps={{
            'aria-label': 'Previous Page',
          }}
          nextIconButtonProps={{
            'aria-label': 'Next Page',
          }}
          onChangePage={this.handleChangePage}
          onChangeRowsPerPage={this.handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 15]}
        />
      </Paper>
    );
  }
}

export default DataTable;

const TableWrapper = styled.div`
  overflow-x: auto;
`;



// WEBPACK FOOTER //
// ./src/components/DataTable/DataTable.js